import { useEffect, useRef } from 'react';

const reveal = (element: Element) => element.classList.add('is-visible');

/** True when the element is in the viewport or has already been scrolled past. */
const isInOrAboveViewport = (element: Element) => element.getBoundingClientRect().top < window.innerHeight;

/**
 * Adds `is-visible` to the element the first time it scrolls into view, which triggers the
 * `.reveal` entrance transition in src/index.css. Reduced-motion users see content immediately
 * because the CSS removes the transition and hidden state for them.
 *
 * Content must never stay hidden, so the element is revealed immediately when it is already on
 * screen (or above it) at mount, when any pixel of it intersects, and when a fast scroll or jump
 * carries it past the viewport without an intersecting frame.
 */
export function useReveal<T extends HTMLElement = HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (typeof IntersectionObserver === 'undefined' || isInOrAboveViewport(element)) {
      reveal(element);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
            reveal(entry.target);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0 }
    );
    observer.observe(element);

    // Safety net for jumps that skip over the element entirely (no intersection change fires).
    const onScroll = () => {
      if (isInOrAboveViewport(element)) {
        reveal(element);
        cleanup();
      }
    };
    const cleanup = () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return cleanup;
  }, []);

  return ref;
}
